/* eslint-disable @typescript-eslint/no-explicit-any */

import Masker from 'vanilla-masker';

export const isCpf = (cpf: string): boolean => {
  if (!cpf) {
    return false;
  }

  const maskedCpfRegex = /^\d{3}\.\*\*\*\.\*\*\*-\d{2}$/;

  if (maskedCpfRegex.test(cpf)) {
    return true;
  }

  cpf = cpf.replace(/\D/g, '');

  if (cpf.length !== 11) {
    return false;
  }

  if (checkInvalid(cpf)) {
    const numberValue = cpf.slice(0, -2);
    const dv1 = cpf[9];
    const dv2 = cpf[10];

    if (checkCPFDV(numberValue, dv1) && checkCPFDV(numberValue + dv1, dv2)) {
      return true;
    }
    return false;
  }

  return false;
};

export const isCNPJ = (cnpj: string) => {
  cnpj = cnpj.replace(/\D/g, '');
  if (cnpjCheckInvalid(cnpj)) {
    const numberValue = cnpj.toString().slice(0, -2);
    const dv1 = cnpj.toString()[12];
    const dv2 = cnpj.toString()[13];

    if (checkCNPJDV(numberValue, dv1) && checkCNPJDV(numberValue + dv1, dv2)) {
      return true;
    }
    return false;
  }
  return false;
};

const checkInvalid = (numberParam: string) => {
  const cpf = numberParam.toString();

  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999' ||
    cpf === '00000000191' ||
    cpf === '12345678909'
  ) {
    return false;
  }
  return true;
};

const checkCPFDV = (numberParam: string, dv: string) => {
  const check = numberParam
    .split('')
    .map((item: any, i) => {
      const res = item * (numberParam.length + 1 - i);

      return res;
    })
    .reduce((x, y) => {
      return x + y;
    });

  let result = 11 - (check % 11);

  if (result === 10 || result === 11) {
    result = 0;
  }

  return result.toString() === dv;
};

const cnpjCheckInvalid = (numberParam: string) => {
  const cnpj = numberParam.toString();

  if (
    cnpj === '00000000000000' ||
    cnpj === '11111111111111' ||
    cnpj === '22222222222222' ||
    cnpj === '33333333333333' ||
    cnpj === '44444444444444' ||
    cnpj === '55555555555555' ||
    cnpj === '66666666666666' ||
    cnpj === '77777777777777' ||
    cnpj === '88888888888888' ||
    cnpj === '99999999999999' ||
    cnpj.length !== 14
  ) {
    return false;
  }
  return true;
};

const checkCNPJDV = (numberParam: string, dv: string) => {
  const check = numberParam
    .split('')
    .reverse()
    .map((item, i) => {
      return ((i % 8) + 2) * parseInt(item, 10);
    })
    .reduce((x, y) => {
      return x + y;
    });

  let result = check % 11;

  if (result < 2) {
    result = 0;
  } else {
    result = 11 - result;
  }

  return result.toString() === dv;
};

export const maskPhone = (value: string) => {
  if (!value) return '';
  const cleanValue = value.replace(/\D/g, '');

  if (cleanValue.length < 10 || cleanValue.length > 11) return value;
  return Masker.toPattern(cleanValue.substring(2, 13), '(99) 9 9999-9999');
};

export const maskPhoneInput = (value: string) =>
  Masker.toPattern(value, '(99) 9 9999-9999');

export const maskCpf = (value: string) => {
  return value ? Masker.toPattern(value, '999.999.999-99') : '';
};

export const maskCEP = (value: string) => {
  return value ? Masker.toPattern(value, '99999-999') : '';
};

export const maskHiddenCpf = (value: string) => {
  const cleanValue = value.replace(/\D/g, '');
  if (!cleanValue || cleanValue.length !== 11) return '';
  return `${cleanValue.substring(0, 3)}.***.***-${cleanValue.substring(9, 11)}`;
};

export const maskHiddenCnpj = (value: string) => {
  const cleanValue = value.replace(/\D/g, '');
  if (!cleanValue || cleanValue.length !== 14) return '';
  return `${cleanValue.substring(0, 2)}.***.***/****-${cleanValue.substring(
    12,
    14
  )}`;
};

export const maskHiddenCpfOrCnpj = (value: string) => {
  if (isCpf(value)) {
    return maskHiddenCpf(value);
  }

  if (isCNPJ(value)) {
    return maskHiddenCnpj(value);
  }

  return value;
};

export const unmaskField = (value?: string | null) => {
  return value ? value.replace(/\D/g, '') : '';
};

export const maskCnpj = (value: string) =>
  value ? Masker.toPattern(value, '99.999.999/9999-99') : '';

export const maskCpfOrCnpj = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const onlyDigitsValue = value.replace(/\D/g, '');

  return isCpf(onlyDigitsValue) || onlyDigitsValue.length <= 11
    ? maskCpf(onlyDigitsValue)
    : maskCnpj(onlyDigitsValue);
};

export const maskCpfOrCnpjHide = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const cpfFormattedRegex = /^\d{3}\.\*\*\*\.\*\*\*-\d{2}$/;
  const cnpjFormattedRegex = /^\d{2}\.\*\*\*\.\*\*\*\/\*\*\*\*-\d{2}$/;

  if (cpfFormattedRegex.test(value) || cnpjFormattedRegex.test(value)) {
    return value;
  }

  const onlyDigitsValue = value.replace(/\D/g, '');

  if (onlyDigitsValue.length <= 11) {
    return onlyDigitsValue.replace(/(\d{3})\d{6}(\d{2})/, '$1.***.***-$2');
  } else if (onlyDigitsValue.length === 14) {
    return onlyDigitsValue.replace(
      /(\d{2})\d{3}\d{3}\d{4}(\d{2})/,
      '$1.***.***/****-$2'
    );
  }

  return value;
};

export const removeSpecialChars = (value: string) =>
  value
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/gi, '') || '';

export const maskBankSlip = (value: string) => {
  return value
    ? Masker.toPattern(
        value,
        '999999999999.9.9999999999 9 999999999.999999999999999'
      )
    : '';
};

export const checkCpfFormat = (value: string) =>
  value.match(/(\d{3}).(\d{3}).(\d{3})-(\d{2})/);

export const checkCnpjFormat = (value: string) =>
  value.match(/(\d{2}).(\d{3}).(\d{3})\/(\d{4})-(\d{2})/);

export const maskCNAE = (value: string) =>
  value ? Masker.toPattern(value, '9999-9/99') : '';

export const maskMoney = (value: string) =>
  value ? Masker.toMoney(value, { unit: 'R$' }) : '';

export const maskPercent = (value: string) =>
  value ? Masker.toMoney(value, { suffixUnit: '%' }) : '';

export const maskDecimal = (value: string) =>
  value ? Masker.toMoney(value, { unit: '' }) : '';

export const addMask = (value: string | number, format: string) =>
  Masker.toPattern(value, { pattern: format });

export const removeAccent = (text: string) =>
  text.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z\s])/g, '');

export const formatMoney = (value: number, fix = true): string => {
  let formatted = '';
  if (!value) {
    if (fix) {
      formatted = '0.00';
    } else {
      formatted = '0';
    }
  } else {
    formatted = value.toFixed(fix ? 2 : 0).toString();
  }
  formatted = formatted.replace(/\./g, ',');
  formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return formatted;
};

export const formatCurrency = (
  value: number,
  fix = true,
  language = 'pt-BR',
  currency = 'BRL'
): string => {
  if (!value) {
    if (fix) {
      return new Intl.NumberFormat(language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: currency ? 'currency' : 'decimal',
        currency: currency || undefined,
      }).format(0);
    } else {
      return new Intl.NumberFormat(language, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        style: currency ? 'currency' : 'decimal',
        currency: currency || undefined,
      }).format(0);
    }
  }

  return new Intl.NumberFormat(language, {
    minimumFractionDigits: fix ? 2 : 0,
    maximumFractionDigits: fix ? 2 : 0,
    style: currency ? 'currency' : 'decimal',
    currency: currency || undefined,
  }).format(value);
};

const formatRandomPixKey = (key: string) =>
  key
    .replace('-', '')
    .replace(/(\w{8})(\w)/, '$1-$2')
    .replace(/(-\w{4})(\w)/, '$1-$2')
    .replace(/(-\w{4})(\w)/, '$1-$2')
    .replace(/(-\w{4})(\w)/, '$1-$2');

const formatPhonePixKey = (key: string) => {
  const clean = key.replace(/\D/g, '').replace(/^55/, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return key;
};

export const formatPixKeyByType = (
  key: string | undefined,
  type: number
): string | undefined => {
  if (!key) return undefined;

  const PixType = {
    TAX_IDENTIFIER: 1,
    EMAIL: 2,
    PHONE: 3,
    RANDOM: 4,
    BANK_DATA: 5,
  };

  if (type === PixType.PHONE) {
    return formatPhonePixKey(key);
  }
  if (type === PixType.TAX_IDENTIFIER) {
    return maskCpfOrCnpj(key);
  }
  if (type === PixType.EMAIL || type === PixType.BANK_DATA) {
    return key;
  }
  if (type === PixType.RANDOM && key.replace(/-/g, '').length === 32) {
    return formatRandomPixKey(key);
  }

  return key;
};

export const formatPixKey = (key: string | undefined) => {
  if (!key) return;

  if (key.replace('-', '').length === 32) {
    return formatRandomPixKey(key);
  }

  if (isCpf(key)) {
    return maskCpf(key);
  }

  if (isCNPJ(key)) {
    return maskCnpj(key);
  }

  const phoneDigits = key.replace(/\D/g, '');
  if (
    phoneDigits.startsWith('55') &&
    (phoneDigits.length === 13 || phoneDigits.length === 12)
  ) {
    return formatPhonePixKey(key);
  }

  return key;
};

export const isValidCpfOrCnpj = (value: string) => {
  const onlyDigitsValue = value.replace(/\D/g, '');
  return isCpf(onlyDigitsValue) || isCNPJ(onlyDigitsValue);
};
